import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Form, Input } from "antd";
import { observer } from "mobx-react-lite";
import { EUserRole, ServiceTemplateField, TRANSLATABLE_SERVICE_TEMPLATE_ATTRIBUTES_LIST, TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { useTranslation } from "../../../../../../others/i18n.js";
import { useStores } from "../../../../../../store/root.js";
import { defaultTranslationFlexNamePath, isExtendedFieldHidden } from "../../../../../../util/template/form.js";
import { getDisableStrategies } from "../../../../../../util/template/merge-strategies.js";
import { getFieldNameTranslated } from "../../../../../../util/template/translation.js";
import { normalizeUrlWithProtocol } from "../../../../../../util/template/url.js";
import { getInputPropsDefaultLang, hasTranslationFlagsChanged } from "../../../../../../util/translation.js";
import { TranslationInputFormFields } from "../../fields/translation/language-input/language-input-form-fields.js";
import { FormFieldVisibilityWrapper, FormFieldVisibilityWrapperByCdn } from "../../formFieldVisibilityWrapper.js";
import { TemplateMetaDataExtendsStrategyFormField } from "../../templateMetaDataExtendsStrategyFormField.js";
/**
 * Form field for the provider's legal notice URL.
 * This field allows users to input the URL of the service provider's legal notice page.
 */ const FormFieldServiceTemplateProviderLegalNoticeUrl = observer(()=>{
    const { t } = useTranslation();
    const { rcbAuthenticationStore, rcbUserSettingsStore: { settings: { translationSettings } } } = useStores();
    const { isTranslationMode } = translationSettings;
    // Triggers form item re-render on translation mode toggle
    const lastKnownTranslationMode = isTranslationMode;
    const hasCreatorRole = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    const form = Form.useFormInstance();
    const urlRules = [
        {
            type: "url",
            message: t("Please be sure to enter a valid url format")
        }
    ];
    return /*#__PURE__*/ _jsx(FormFieldVisibilityWrapperByCdn, {
        children: /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
            fieldNames: ServiceTemplateField.ProviderLegalNoticeUrl,
            permissionCheck: {
                whenRole: EUserRole.Translator,
                roleWhiteList: TRANSLATABLE_SERVICE_TEMPLATE_ATTRIBUTES_LIST
            },
            forceHiddenFormCallback: isExtendedFieldHidden(ServiceTemplateField.ProviderLegalNoticeUrl, hasCreatorRole),
            children: /*#__PURE__*/ _jsx(Form.Item, {
                noStyle: true,
                shouldUpdate: (prev, next)=>prev.isProviderCurrentWebsite !== next.isProviderCurrentWebsite || hasTranslationFlagsChanged(prev, next, ServiceTemplateField.ProviderLegalNoticeUrl) || lastKnownTranslationMode !== isTranslationMode,
                children: ({ getFieldValue })=>{
                    return getFieldValue(ServiceTemplateField.IsProviderCurrentWebsite) === false && /*#__PURE__*/ _jsx(Form.Item, {
                        label: getFieldNameTranslated(ServiceTemplateField.ProviderLegalNoticeUrl, t),
                        shouldUpdate: (prev, next)=>prev.extends?.identifier !== next.extends?.identifier,
                        children: ({ getFieldValue })=>{
                            const extended = getFieldValue(TemplateMetaDataField.Extends);
                            return /*#__PURE__*/ _jsxs(TemplateMetaDataExtendsStrategyFormField, {
                                extendsFieldName: ServiceTemplateField.ProviderLegalNoticeUrl,
                                disableStrategies: getDisableStrategies(),
                                onKeepContent: extended && /*#__PURE__*/ _jsx(Input, {
                                    value: extended[ServiceTemplateField.ProviderLegalNoticeUrl],
                                    disabled: true
                                }),
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: ServiceTemplateField.ProviderLegalNoticeUrl,
                                        normalize: (value)=>normalizeUrlWithProtocol(value),
                                        rules: urlRules,
                                        noStyle: true,
                                        children: /*#__PURE__*/ _jsx(Input, {
                                            placeholder: `https://${t("sample-providers.tld/legal")}`,
                                            maxLength: 255,
                                            ...getInputPropsDefaultLang(translationSettings, ServiceTemplateField.ProviderLegalNoticeUrl, form),
                                            disabled: !hasCreatorRole
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TranslationInputFormFields, {
                                        flexPathConfig: {
                                            absolute: {
                                                namePath: defaultTranslationFlexNamePath(ServiceTemplateField.ProviderLegalNoticeUrl)
                                            }
                                        },
                                        inputProps: {
                                            maxLength: 255
                                        },
                                        formProps: {
                                            rules: urlRules,
                                            normalize: (value)=>normalizeUrlWithProtocol(value)
                                        }
                                    })
                                ]
                            });
                        }
                    });
                }
            })
        })
    });
});
export { FormFieldServiceTemplateProviderLegalNoticeUrl };
